import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';
import 'vue-multiselect/dist/vue-multiselect.min.css';

import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import Multiselect from 'vue-multiselect';
import {Component, Vue, Watch} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';

import CardHeader from '@/components/Includes/CardHeader/CardHeader.vue';
import Loader from '@/components/Includes/Loader/Loader.vue';
import Pagination from '@/components/Includes/Pagination/Pagination.vue';
import SelectionColumnsInTable from '@/components/Includes/SelectionColumnsInTable/SelectionColumnsInTable.vue';
import TableEmpty from '@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty.vue';
import formatMoney from '@/filters/formatMoney';
import formatPercent from "@/filters/formatPercent";
import ICountry from '@/interfaces/countries/ICountry';
import IClientApp from "@/interfaces/IClientApp";
import IPayloadQuery from '@/interfaces/IPayloadQuery';
import IQueryRebills from '@/interfaces/IQueryRebills';
import IRebill from '@/interfaces/IRebill';
import ITableCol from '@/interfaces/ITableCol';
import ITracker from "@/interfaces/ITracker";
import IMultiSelectItem from '@/interfaces/selects/IMultiSelectItem';
import ISelectItemDefault from '@/interfaces/selects/ISelectItemDefault';
import capitalizeFirstLetter from '@/libs/capitalizeFirstLetter';
import defaultDate from '@/libs/date/defaultDate';
import getQueryData from '@/libs/getQueryData';
import getTableColumns from '@/libs/getTableColumns';
import getTips from '@/libs/getTips';
import getTotalItem from '@/libs/getTotalItem';
import isEmpty from '@/libs/isEmpty';
import {transformMultiSelect} from '@/libs/transformData/transformMultiSelect';
import transformSelect from "@/libs/transformData/transformSelect";
import {Switch as cSwitch} from '@coreui/vue';

const today = new Date();

@Component({
    name: 'Rebills',
    components: {
        'vue-picker': VueCtkDateTimePicker,
        CardHeader,
        Pagination,
        TableEmpty,
        Loader,
        cSwitch,
        SelectionColumnsInTable,
        Multiselect
    }
})

export default class Rebills extends Vue {
    @Action private trackersList!: () => Promise<void>;
    @Getter private getTrackers!: () => ITracker[];
    @Action private rebillsList!: (query: IPayloadQuery) => number;
    @Getter private getRebills!: () => IRebill[];
    @Action private countriesList!: (query: IPayloadQuery) => Promise<void>;
    @Getter private getCountries!: () => ICountry[];
    @Action private getRebillsGroups!: () => string[];

    // Begin Vuex Logic for Client Apps
    @Action private clientAppsList!: () => Promise<void>;
    @Getter private getClientApps!: () => IClientApp[];
    // End Vuex Logic for Client Apps

    // Begin Config Date Picker
    private date: { start: string, end: string } = {
        start: '',
        end: ''
    };
    private config: any = {
        format: 'YYYY-MM-DD',
        formatted: 'll',
        range: true,
        overlay: true,
        dark: true
    };
    // End Config Date Picker

    private loading: boolean = true;

    private firstLoad: boolean = true;

    private selectedClientApp: any = '';
    private clientApps: IMultiSelectItem[] = [];

    private tracker: any = 1;
    private trackers: any[] = [];
    private rebills: IRebill[] = [];
    private totalRebillsData: any = [];
    private countries: IMultiSelectItem[] = []
    private selectedTableColumns: IMultiSelectItem[] = [];

    // Begin configs for query
    private groupBy: string = '';
    private groupList: ISelectItemDefault[] | string[] = [
        {
            text: 'Date',
            value: 'date'
        }
    ];
    private typeBy: string = '';
    private typesList: ISelectItemDefault[] = [
        {
            text: 'All',
            value: 'all'
        },
        {
            text: 'Paid',
            value: 'paid'
        },
        {
            text: 'Organic',
            value: 'organic'
        }
    ];

    private selectedTimezone: string = '';
    private timezones: ISelectItemDefault[] = [
        {
            text: 'Default',
            value: '+3:00'
        },
        {
            text: 'UTC',
            value: '+0:00'
        }
    ];

    private orderName: string = ''; // sort by selected field
    private orderType: boolean = true; // sort by desc/asc

    private trackerCampaignId: number | null = null;
    private selectedCountries: any = [];
    // End configs for query

    // Begin for pagination
    private totalRebillsItems: number | void | null = null;
    private currentPage: number = 1;
    private perPage: number = 50;
    // End for pagination


    // Begin Date
    private get dateStart() {
        return this.date.start;
    }

    private get dateEnd() {
        return this.date.end === null ? this.dateStart : this.date.end;
    }

    public get selectedDateInterval() {
        return `${this.dateStart} / ${this.dateEnd}`;
    }

    // End Date

    private get tableColumns(): ITableCol[] | undefined {
        if (this.tableItems) {
            return getTableColumns(this.tableItems, true)
        }
    };

    private changedTableColumns(columnsList: IMultiSelectItem[]) {
        this.selectedTableColumns = columnsList
    }

    private get fields() {
        if (this.tableColumns) {
            const columnsIds = this.selectedTableColumns.map((el: IMultiSelectItem) => el.value);
            return this.tableColumns.filter((el: any) => columnsIds.includes(el.key));
        } else {
            return this.tableColumns;
        }
    }

    // Begin find column for total line
    private findColumn(field: any): boolean {
        if (this.fields) {
            const findField = this.fields.findIndex(sourceField => sourceField.key === field)
            return findField !== -1;
        } else {
            return false
        }
    }

    // End find column for total line

    private get isShowTotal() {
        if (this.tableItems) {
            return this.tableItems.length > 0;
        }
    }

    private get tableItems(): IRebill[] {
        const sourceData = Object.values(this.items);
        this.totalRebillsData = getTotalItem(sourceData);
        return sourceData;
    }

    private get items() {
        return this.rebills;
    }

    private get itemsLength() {
        return this.tableItems.length;
    }

    private checkTip(label: string) {
        return getTips(label);
    }

    private checkColumn(field: string, value: string): string {
        const WData = /W[0-9]/g.test(field)
        return WData ? formatMoney(Number(value)) : this.formatColumn(field, value)
    }

    private formatColumn(field: string, value: string): string {
        const fieldsMoney: string[] = [
            'PPS',
            'PPT',
            'CPT',
            'costs',
            'sum'
        ];
        const fieldsPercent: string[] = [
            'ICR',
            'TCR',
            'SCR',
            'ROI'
        ];
        const fieldsCapitalize: string[] = ['group'];

        if (fieldsMoney.includes(field)) {
            return formatMoney(Number(value));
        } else if (fieldsPercent.includes(field)) {
            return formatPercent(Number(value));
        } else if (fieldsCapitalize.includes(field)) {
            return this.nameColumn(value);
        } else {
            return value;
        }
    }

    private nameColumn(name: string): string {
        return capitalizeFirstLetter(name);
    }

    private changeOrderName(sortField: string): void {
        this.orderName = sortField
    }

    private cleanData(data: { [index: string]: any }) {
        return Object.keys(data)
            .filter(el => !isEmpty(data[el]))
            .reduce((obj: { [index: string]: any }, item: string) => {
                obj[item] = data[item];
                return obj;
            }, {});
    }

    private async transformQueryCountries(queryString: string) {
        const countryIdsFromQuery = queryString.split(',');

        await this.getCountriesList();

        if (this.countries) {
            const filteredCountries = this.countries.filter((el: any) => countryIdsFromQuery.includes(el.value));
            this.selectedCountries = this.selectedCountries.length > 0 ? this.selectedCountries : filteredCountries
        }
    }

    private getCountriesForQuery() {
        return this.selectedCountries.map((item: { label: string, value: string }) => item.value).join()
    }

    private checkClientApp(clientAppId: number) {
        return this.selectedClientApp ? this.selectedClientApp : this.clientApps.find((clientApp: IMultiSelectItem) => clientApp.value === Number(clientAppId));
    }

    private readQueryValue() {
        const queryData = getQueryData(this.$route.query);

        queryData.map(({key, value}) => {
            switch (key) {
                case 'group': {
                    this.groupBy = this.groupBy ? this.groupBy : value
                    break;
                }
                case 'type': {
                    this.typeBy = this.typeBy ? this.typeBy : value
                    break;
                }
                case 'date_start': {
                    this.date.start = this.dateStart ? this.dateStart : value
                    break;
                }
                case 'date_end': {
                    this.date.end = this.dateEnd ? this.dateEnd : value
                    break;
                }
                case 'tracker_campaign_id': {
                    this.trackerCampaignId = this.trackerCampaignId ? this.trackerCampaignId : value
                    break;
                }
                case 'country': {
                    this.transformQueryCountries(value);
                    break;
                }
                case 'client_app_id': {
                    this.selectedClientApp = this.checkClientApp(value);
                    break;
                }
                case 'timezone': {
                    this.selectedTimezone = this.selectedTimezone ? this.selectedTimezone : value;
                    break;
                }
                case 'order_name': {
                    this.orderName = this.orderName ? this.orderName : value
                    break;
                }
                case 'order_type': {
                    this.orderType = this.orderType ? this.orderType : Boolean(value)
                    break;
                }
            }
        });
    }

    private queryValue() {
        if (this.payloadQuery.query && this.payloadQuery.query.length > 0) {
            this.readQueryValue();
        } else {
            this.getDefaultDate();
            this.writeQueryValue();
            this.readQueryValue();
        }
    }

    @Watch('groupBy')
    @Watch('typeBy')
    @Watch('dateStart')
    @Watch('dateEnd')
    @Watch('trackerCampaignId')
    @Watch('selectedTimezone')
    @Watch('selectedClientApp')
    @Watch('selectedCountries')
    @Watch('orderName')
    @Watch('tracker')
    @Watch('orderType')
    private writeQueryValue() {

        const queryData: any = {
            group: this.groupBy ? this.groupBy : 'date',
            type: this.typeBy ? this.typeBy : 'all',
            date_start: this.dateStart,
            date_end: this.dateEnd,
            tracker: this.tracker,
            order_name: this.orderName.length !== 0 ? this.orderName : 'group',
            order_type: this.orderType ? 'desc' : 'asc',
            client_app_id: this.selectedClientApp ? Number(this.selectedClientApp.value) : null,
            tracker_campaign_id: this.trackerCampaignId,
            country: this.selectedCountries ? this.getCountriesForQuery() : '',
            timezone: this.selectedTimezone ? this.selectedTimezone : '+3:00',
            pagination: false
        };

        this.$router.replace({
            name: this.$route.name || undefined,
            query: this.cleanData(queryData)
        });
    }

    private get payloadQuery(): IPayloadQuery {
        return {
            query: this.$route.fullPath.split(this.$route.path)[1]
        }
    }

    // @Watch('orderName')
    // @Watch('orderType')
    private async loadRebills() {
        this.loading = true;
        this.firstLoad = false;

        try {
            await this.rebillsList(this.payloadQuery);
            this.rebills = this.getRebills();
            this.loading = false;
        } catch {
            this.loading = false;
            Promise.reject('Error by rebills data')
        }
    }

    private transformMultySelects(data: any) {
        const fieldName = 'name';
        const fieldValue = 'code';
        return transformMultiSelect(data, fieldName, fieldValue);
    }

    private async getCountriesList() {
        try {
            this.countries = this.transformMultySelects(this.getCountries());

            if (this.countries.length === 0) {
                await this.countriesList({query: '?pagination=false'});
                this.countries = this.transformMultySelects(this.getCountries());
            }
        } catch {
            return Promise.reject('Error get countries');
        }
    }

    private async getClientAppsList() {
        try {
            this.clientApps = transformMultiSelect(this.getClientApps(), 'name', 'id');

            if (isEmpty(this.clientApps)) {
                await this.clientAppsList();
                this.clientApps = transformMultiSelect(this.getClientApps(), 'name', 'id');
            }
        } catch (error) {
            throw new Error(error)
        }
    }

    private async getGroups() {
        this.groupList = await this.getRebillsGroups();
        this.groupList = this.groupList.map((item: string) => {
            return {
                text: capitalizeFirstLetter(item),
                value: item,
            }
        });
    }

    private getDefaultDate() {
        const initDate = defaultDate(14, false);
        if (initDate) {
            this.date.start = initDate.start;
            this.date.end = initDate.end;
        }
    }

    private async loadTrackers(): Promise<void> {
        this.trackers = transformSelect(this.getTrackers(), 'name', 'id');
        if (this.trackers.length === 0) {
            await this.trackersList();
            this.trackers = transformSelect(this.getTrackers(), 'name', 'id');
        }
        this.trackers.push({text: 'all', value: 'all'})
    }

    private async created() {
        await this.loadTrackers();
        this.loading = false;
    }

    private mounted() {
        this.getCountriesList();
        this.queryValue();
        this.getGroups();
        this.getClientAppsList();
    }
}
